import { createSelector } from '@reduxjs/toolkit';
import { RegistrationSliceState } from './state';

/**
 * Base selector for the registration slice state
 */
const selectRegistrationSlice = (state: { registration: RegistrationSliceState }) => state.registration;

/**
 * Select the current registration
 */
export const selectCurrentRegistration = createSelector(
  [selectRegistrationSlice],
  (registrationState) => registrationState.currentRegistration
);

/**
 * Select whether the registration has been edited
 */
export const selectRegistrationIsEdited = createSelector(
  [selectRegistrationSlice],
  (registrationState) => registrationState.isEdited
);

/**
 * Select the list of edited field paths
 */
export const selectEditedFields = createSelector(
  [selectRegistrationSlice],
  (registrationState) => registrationState.editedFields
);

/**
 * Select all validation errors
 */
export const selectValidationErrors = createSelector(
  [selectRegistrationSlice],
  (registrationState) => registrationState.validationErrors
);

/**
 * Select addresses from the current registration
 */
export const selectAddresses = createSelector(
  [selectCurrentRegistration],
  (registration) => registration?.addresses ?? []
);

/**
 * Select name from the current registration
 */
export const selectName = createSelector(
  [selectCurrentRegistration],
  (registration) => registration?.name ?? {}
);

/**
 * Select demographics from the current registration
 */
export const selectDemographics = createSelector(
  [selectCurrentRegistration],
  (registration) => registration?.demographics ?? {}
);

/**
 * Select contact info from the current registration
 */
export const selectContactInfo = createSelector(
  [selectCurrentRegistration],
  (registration) => registration?.contactInfo ?? {}
);

/**
 * Select identifiers from the current registration
 */
export const selectIdentifiers = createSelector(
  [selectCurrentRegistration],
  (registration) => registration?.identifiers ?? {}
);

/**
 * Check if a specific field has been edited
 */
export const selectIsFieldEdited = (fieldPath: string) =>
  createSelector(
    [selectEditedFields],
    (editedFields) => editedFields.includes(fieldPath)
  );

/**
 * Select validation error for a specific field
 */
export const selectFieldValidationError = (fieldPath: string) =>
  createSelector(
    [selectValidationErrors],
    (validationErrors) => validationErrors[fieldPath]
  );
